class FriendsHeaderSearch extends HTMLElement {
  connectedCallback() {
    if (this.dataset.friendsSearchReady === 'true') {
      return;
    }

    this.dataset.friendsSearchReady = 'true';
    this.form = this.querySelector('form');
    this.input = this.querySelector('[name="q"]');
    this.clearButton = this.querySelector('.friends-header-search__clear');
    this.overlay = this.querySelector('.friends-header-search__overlay');
    this.panel = this.querySelector('.friends-header-search__panel');
    this.loading = this.querySelector('.friends-header-search__loading');
    this.results = this.querySelector('.friends-header-search__results');
    this.minimumQueryLength = Number(this.dataset.minimumQueryLength || 2);
    this.searchSection = this.dataset.searchSection;
    this.predictiveSearchUrl = this.dataset.predictiveSearchUrl;
    this.abortController = null;
    this.debounceTimer = null;
    this.pointerDownInside = false;
    this.boundDocumentPointerDown = (event) => this.onDocumentPointerDown(event);
    this.boundDocumentPointerEnd = () => {
      this.pointerDownInside = false;
    };
    this.boundResize = () => this.positionPanel();
    this.boundScroll = () => this.dismiss();

    this.input.addEventListener('input', () => this.queueSearch());
    this.input.addEventListener('focus', () => {
      this.activate();
      this.reopenResults();
    });
    this.overlay.addEventListener('pointerdown', (event) => {
      event.preventDefault();
      this.dismiss();
      this.input.blur();
    });
    this.addEventListener('pointerdown', () => {
      this.pointerDownInside = true;
    });
    this.addEventListener('focusout', (event) => {
      // Safari may leave focus on the input while a result link is clicked.
      // Capture the pointer origin now because a frame may run before click.
      const pointerStartedWithinSearch = this.pointerDownInside;

      window.requestAnimationFrame(() => {
        const focusRemainsWithinSearch = event.relatedTarget instanceof Node && this.contains(event.relatedTarget);

        if (pointerStartedWithinSearch || focusRemainsWithinSearch) {
          return;
        }

        if (!this.contains(document.activeElement)) {
          this.dismiss();
        }
      });
    });
    this.form.addEventListener('reset', () => window.requestAnimationFrame(() => this.reset()));
    this.addEventListener('keydown', (event) => this.onKeyDown(event));
    document.addEventListener('pointerdown', this.boundDocumentPointerDown);
    document.addEventListener('pointerup', this.boundDocumentPointerEnd);
    document.addEventListener('pointercancel', this.boundDocumentPointerEnd);
    window.addEventListener('resize', this.boundResize, { passive: true });
    window.addEventListener('scroll', this.boundScroll, { passive: true });
  }

  disconnectedCallback() {
    this.abortController?.abort();
    window.clearTimeout(this.debounceTimer);
    document.removeEventListener('pointerdown', this.boundDocumentPointerDown);
    document.removeEventListener('pointerup', this.boundDocumentPointerEnd);
    document.removeEventListener('pointercancel', this.boundDocumentPointerEnd);
    window.removeEventListener('resize', this.boundResize);
    window.removeEventListener('scroll', this.boundScroll);
  }

  queueSearch() {
    window.clearTimeout(this.debounceTimer);
    this.clearButton.hidden = this.input.value.length === 0;

    const term = this.input.value.trim();

    if (term.length < this.minimumQueryLength) {
      this.abortController?.abort();
      this.input.removeAttribute('aria-busy');
      this.loading.hidden = true;
      this.results.replaceChildren();
      this.close();
      return;
    }

    this.showLoading();
    this.debounceTimer = window.setTimeout(() => this.search(term), 200);
  }

  async search(term) {
    this.abortController?.abort();
    this.abortController = new AbortController();

    const url = new URL(this.predictiveSearchUrl, window.location.origin);
    url.searchParams.set('q', term);
    url.searchParams.set('resources[type]', 'product,query,collection');
    url.searchParams.set('resources[limit]', '6');
    url.searchParams.set('resources[limit_scope]', 'each');
    url.searchParams.set('section_id', this.searchSection);

    try {
      const response = await fetch(url.toString(), {
        headers: { Accept: 'text/html' },
        signal: this.abortController.signal
      });

      if (!response.ok) {
        throw new Error(`Predictive search returned ${response.status}`);
      }

      const responseDocument = new DOMParser().parseFromString(await response.text(), 'text/html');
      const responseResults = responseDocument.querySelector('[data-friends-predictive-results]');

      if (this.input.value.trim() !== term) {
        return;
      }

      if (!responseResults) {
        this.input.removeAttribute('aria-busy');
        this.loading.hidden = true;
        this.close();
        return;
      }

      this.results.innerHTML = responseResults.innerHTML;
      this.input.removeAttribute('aria-busy');
      this.loading.hidden = true;
      this.open();
    } catch (error) {
      if (error.name !== 'AbortError') {
        this.input.removeAttribute('aria-busy');
        this.close();
      }
    }
  }

  showLoading() {
    this.results.replaceChildren();
    this.input.setAttribute('aria-busy', 'true');
    this.loading.hidden = false;
    this.open();
  }

  reopenResults() {
    if (this.input.value.trim().length < this.minimumQueryLength) {
      return;
    }

    if (this.loading.hidden === false || this.results.childElementCount > 0) {
      this.open();
    } else {
      this.queueSearch();
    }
  }

  open() {
    this.activate();
    this.panel.hidden = false;
    this.input.setAttribute('aria-expanded', 'true');
    this.positionPanel();
  }

  close() {
    if (!this.panel) {
      return;
    }

    this.panel.hidden = true;
    this.input.setAttribute('aria-expanded', 'false');
  }

  activate() {
    this.classList.add('is-active');
    this.positionPanel();
  }

  deactivate() {
    this.classList.remove('is-active');
  }

  dismiss() {
    this.abortController?.abort();
    window.clearTimeout(this.debounceTimer);
    this.input.removeAttribute('aria-busy');
    this.loading.hidden = true;
    this.close();
    this.deactivate();
  }

  reset() {
    this.abortController?.abort();
    window.clearTimeout(this.debounceTimer);
    this.clearButton.hidden = true;
    this.input.removeAttribute('aria-busy');
    this.loading.hidden = true;
    this.results.replaceChildren();
    this.close();
    this.input.focus();
  }

  positionPanel() {
    const headerSection = this.closest('.shopify-section--header');

    if (!headerSection) {
      return;
    }

    const fieldBounds = this.getBoundingClientRect();
    const headerBounds = headerSection.getBoundingClientRect();
    const viewportPadding = 24;
    const availableWidth = Math.max(280, window.innerWidth - fieldBounds.left - viewportPadding);

    this.panel.style.top = `${Math.max(0, headerBounds.bottom)}px`;
    this.panel.style.left = `${Math.max(viewportPadding, fieldBounds.left)}px`;
    this.panel.style.width = `${Math.min(640, availableWidth)}px`;
    this.style.setProperty('--friends-search-overlay-top', `${Math.max(0, headerBounds.bottom)}px`);
  }

  onDocumentPointerDown(event) {
    if (!this.contains(event.target)) {
      this.pointerDownInside = false;
      this.dismiss();
    }
  }

  onKeyDown(event) {
    const resultLinks = Array.from(this.querySelectorAll('[data-friends-search-result]'));
    const currentIndex = resultLinks.indexOf(document.activeElement);

    if (event.key === 'Escape') {
      this.input.focus();
      this.dismiss();
      return;
    }

    if (event.key === 'ArrowDown') {
      if (this.panel.hidden || resultLinks.length === 0) {
        return;
      }

      event.preventDefault();
      resultLinks[currentIndex < 0 ? 0 : (currentIndex + 1) % resultLinks.length].focus();
      return;
    }

    if (event.key === 'ArrowUp' && currentIndex >= 0) {
      event.preventDefault();

      if (currentIndex === 0) {
        this.input.focus();
      } else {
        resultLinks[currentIndex - 1].focus();
      }
    }
  }
}

if (!window.customElements.get('friends-header-search')) {
  window.customElements.define('friends-header-search', FriendsHeaderSearch);
}

class FriendsDesktopNavigationCoordinator {
  constructor(navigation) {
    this.navigation = navigation;
    this.topLevelList = navigation.querySelector(':scope > .header__linklist');

    if (!this.topLevelList) {
      return;
    }

    navigation.addEventListener('pointerover', (event) => this.onPointerOver(event));
    navigation.addEventListener('pointerout', (event) => this.onPointerOut(event));
    navigation.addEventListener('focusin', (event) => this.onFocusIn(event));
    navigation.addEventListener('focusout', () => this.onFocusOut());
    navigation.addEventListener('keydown', (event) => this.onKeyDown(event));
  }

  getTopLevelItem(node) {
    if (!(node instanceof Element)) {
      return null;
    }

    const item = node.closest('.header__linklist-item.has-dropdown');
    return item?.parentElement === this.topLevelList ? item : null;
  }

  onPointerOver(event) {
    const item = this.getTopLevelItem(event.target);

    if (!item || item.contains(event.relatedTarget)) {
      return;
    }

    this.closeAllExcept(item);
  }

  onPointerOut(event) {
    const item = this.getTopLevelItem(event.target);

    if (!item || item.contains(event.relatedTarget)) {
      return;
    }

    this.closeItem(item);
  }

  onFocusIn(event) {
    const item = this.getTopLevelItem(event.target);

    if (item) {
      this.closeAllExcept(item);
    }
  }

  onFocusOut() {
    window.requestAnimationFrame(() => {
      if (!this.navigation.contains(document.activeElement)) {
        this.closeAllExcept();
      }
    });
  }

  onKeyDown(event) {
    if (event.key !== 'Escape') {
      return;
    }

    const item = this.getTopLevelItem(event.target);

    if (!item) {
      return;
    }

    this.closeItem(item);
    item.querySelector(':scope > .header__linklist-link')?.focus();
  }

  closeAllExcept(exception) {
    this.topLevelList.querySelectorAll(':scope > .header__linklist-item.has-dropdown').forEach((item) => {
      if (item !== exception) {
        this.closeItem(item);
      }
    });
  }

  closeItem(item) {
    Array.from(item.querySelectorAll('[aria-controls][aria-expanded="true"]')).reverse().forEach((trigger) => {
      const panel = document.getElementById(trigger.getAttribute('aria-controls'));

      trigger.setAttribute('aria-expanded', 'false');

      if (panel && item.contains(panel)) {
        panel.hidden = true;
        panel.classList.remove('is-closing');
      }
    });
  }
}

function initializeFriendsDesktopNavigation(root = document) {
  root.querySelectorAll('store-header[data-header-layout="friends_logo_center_inline_search"] desktop-navigation').forEach((navigation) => {
    if (navigation.dataset.friendsNavigationReady === 'true') {
      return;
    }

    navigation.dataset.friendsNavigationReady = 'true';
    new FriendsDesktopNavigationCoordinator(navigation);
  });
}

initializeFriendsDesktopNavigation();
document.addEventListener('shopify:section:load', (event) => initializeFriendsDesktopNavigation(event.target));

class FriendsMobileHeaderBehavior {
  constructor() {
    this.mobileMedia = window.matchMedia('(max-width: 999px)');
    this.lastScrollY = Math.max(0, window.scrollY);
    this.direction = null;
    this.directionDistance = 0;
    this.rafPending = false;
    this.headerSection = document.querySelector('.shopify-section--header');
    this.facetToolbar = this.getFacetToolbar();
    this.boundMediaChange = () => this.onMediaChange();

    window.addEventListener('scroll', () => this.onScroll(), { passive: true });

    if (this.mobileMedia.addEventListener) {
      this.mobileMedia.addEventListener('change', this.boundMediaChange);
    } else {
      this.mobileMedia.addListener(this.boundMediaChange);
    }

    document.addEventListener('shopify:section:load', () => this.refreshHeader());
  }

  onMediaChange() {
    this.lastScrollY = Math.max(0, window.scrollY);
    this.direction = null;
    this.directionDistance = 0;
    this.show();
  }

  refreshHeader() {
    this.headerSection = document.querySelector('.shopify-section--header');
    this.facetToolbar = this.getFacetToolbar();
    this.show();
  }

  onScroll() {
    if (this.rafPending) {
      return;
    }

    this.rafPending = true;

    window.requestAnimationFrame(() => {
      this.update();
      this.rafPending = false;
    });
  }

  update() {
    const currentScrollY = Math.max(0, window.scrollY);
    const delta = currentScrollY - this.lastScrollY;
    this.lastScrollY = currentScrollY;

    if (!this.mobileMedia.matches || !this.isSticky() || this.mustRemainVisible() || currentScrollY <= 12) {
      this.directionDistance = 0;
      this.show();
      return;
    }

    if (Math.abs(delta) < 2) {
      return;
    }

    const nextDirection = delta > 0 ? 'down' : 'up';

    if (nextDirection !== this.direction) {
      this.direction = nextDirection;
      this.directionDistance = 0;
    }

    this.directionDistance += Math.abs(delta);

    if (nextDirection === 'down' && currentScrollY > this.headerSection.offsetHeight && this.directionDistance >= 14) {
      this.hide();
      this.directionDistance = 0;
    } else if (nextDirection === 'up' && this.directionDistance >= 8) {
      this.show();
      this.directionDistance = 0;
    }
  }

  isSticky() {
    return Boolean(this.headerSection?.querySelector('store-header[sticky]'));
  }

  getFacetToolbar() {
    const pageType = window.themeVariables?.settings?.pageType;

    if (pageType !== 'collection' && pageType !== 'search') {
      return null;
    }

    return document.getElementById('mobile-facet-toolbar');
  }

  mustRemainVisible() {
    if (window.Shopify?.designMode || document.documentElement.classList.contains('lock-all')) {
      return true;
    }

    return Boolean(document.querySelector('#mobile-menu-drawer[open], #search-drawer[open], #mini-cart[open]'));
  }

  hide() {
    this.headerSection?.classList.add('friends-mobile-header--hidden');
    this.facetToolbar?.classList.add('friends-mobile-facet-toolbar--hidden');
  }

  show() {
    this.headerSection?.classList.remove('friends-mobile-header--hidden');
    this.facetToolbar?.classList.remove('friends-mobile-facet-toolbar--hidden');
  }
}

new FriendsMobileHeaderBehavior();

// Focal waits before activating the drawer focus trap. On mobile Safari that
// delay can prevent the keyboard from opening because focus is no longer tied
// to the visitor's original tap. Open and focus synchronously instead.
document.addEventListener('click', (event) => {
  if (!window.matchMedia('(max-width: 999px)').matches) {
    return;
  }

  if (!(event.target instanceof Element)) {
    return;
  }

  const trigger = event.target.closest('[aria-controls="search-drawer"]');

  if (!trigger) {
    return;
  }

  const drawer = document.getElementById('search-drawer');
  const input = drawer?.querySelector('[name="q"]');

  if (!drawer || !input || drawer.hasAttribute('open')) {
    return;
  }

  event.preventDefault();
  event.stopPropagation();
  drawer.setAttribute('open', '');
  trigger.setAttribute('aria-expanded', 'true');
  // Mobile CSS reveals the input without moving it. Resolve that style now,
  // in the trusted click, rather than waiting for a frame or transition.
  drawer.getBoundingClientRect();
  // Capture the opener before focusing the input so closing returns focus to
  // the search control. Focal's later activation is a no-op for an active trap.
  trigger.focus({ preventScroll: true });
  drawer.focusTrap?.activate();
  input.focus({ preventScroll: true });
  input.setSelectionRange(input.value.length, input.value.length);
}, true);
